import React, { useState, useEffect, useContext } from "react";
import DualPricing from "./DualPricing";
import Surcharge from "./Surcharge";
import { getEmsBootEquipments, getEquipments } from "../../../../api";
import NewToProcessing from "./NewToProcessing";
import { IoMdCheckmark } from "react-icons/io";
import CustomRateDesk from "./CustomRateDesk";
import FlatRatePricing from "./FlatRatePricing";
import TieredPricing from "./TieredPricing";
import ICPlusPricing from "./ICPlusPricing";
import ACHPricing from "./ACHPricing";
import { Stack } from "@mui/material";
import ToggleSwitch from "./input-elements/ToggleSwitch";
import { EquipmentContext } from "../useContext/context";
import config from "../../../../config";

const renderModel = (
    model,
    emsEquipments,
    setFormDataByKeyValue,
    errors,
    form_data,
    allEquipments,
    removeError,
    isEditable = true,
) => {
    switch (model) {
        case "dual_pricing":
            return (
                <DualPricing
                    isEditable={isEditable}
                    emsEquipments={emsEquipments}
                    setFormDataByKeyValue={setFormDataByKeyValue}
                    errors={errors}
                    form_data={form_data}
                    allEquipments={allEquipments}
                    removeError={removeError}
                />
            );
        case "surcharge":
            return (
                <Surcharge
                    isEditable={isEditable}
                    emsEquipments={emsEquipments}
                    setFormDataByKeyValue={setFormDataByKeyValue}
                    errors={errors}
                    form_data={form_data}
                    allEquipments={allEquipments}
                    removeError={removeError}
                />
            );
        case "flat_rate":
            return (
                <FlatRatePricing
                    isEditable={isEditable}
                    emsEquipments={emsEquipments}
                    setFormDataByKeyValue={setFormDataByKeyValue}
                    errors={errors}
                    form_data={form_data}
                    allEquipments={allEquipments}
                    removeError={removeError}
                />
            );
        case "tiered":
            return (
                <TieredPricing
                    isEditable={isEditable}
                    emsEquipments={emsEquipments}
                    setFormDataByKeyValue={setFormDataByKeyValue}
                    errors={errors}
                    form_data={form_data}
                    allEquipments={allEquipments}
                    removeError={removeError}
                />
            );
        case "ic_plus":
            return (
                <ICPlusPricing
                    isEditable={isEditable}
                    emsEquipments={emsEquipments}
                    setFormDataByKeyValue={setFormDataByKeyValue}
                    errors={errors}
                    form_data={form_data}
                    allEquipments={allEquipments}
                    removeError={removeError}
                />
            );
        case "ach":
            return (
                <ACHPricing
                    isEditable={isEditable}
                    emsEquipments={emsEquipments}
                    setFormDataByKeyValue={setFormDataByKeyValue}
                    errors={errors}
                    form_data={form_data}
                    allEquipments={allEquipments}
                    removeError={removeError}
                />
            );
        default:
            return "";
    }
};

const PricingModel = ({
    pricingModels,
    setFormDataByKeyValue,
    errors,
    form_data,
    allEquipments,
    removeError,
    isEditable = true,

}) => {
    const [selectedModel, setSelectedModel] = useState(form_data.pricing_model);
    const [emsEquipments, setEmsEquipments] = useState([]);
    const { org_id, pricingDefaults } = useContext(EquipmentContext);

    useEffect(() => {
        getEmsBootEquipments({ org_id }).then((res) => {
            if (res.status) {
                setEmsEquipments(res.data);
            } else {
                console.log(res.message);
            }
        });
    }, []);

    const handlePricingModelChange = (model) => {
        if (isEditable) {
            setSelectedModel(model.type);
            setFormDataByKeyValue("pricing_model", model.type);
        }
    };

    useEffect(() => {
        setSelectedModel(form_data.pricing_model);
    }, [form_data]);

    // Get active pricing models from organization defaults, fallback to all models
    const activePricingModels = pricingDefaults?.active_pricing_models 
        ? Object.keys(pricingDefaults.active_pricing_models).filter(key => pricingDefaults.active_pricing_models[key] === 1)
        : pricingModels.map(model => model.type);

    return (
        <div className="pricing-model-container mt-3r">
            <h6 className="sub-heading">Pricing Model</h6>
            <p className="sub-heading-lead">
                Select your preferred pricing model
            </p>

            <Stack
                direction={{ xs: "column", sm: "row" }}
                className="pricing-model-buttons"
            >
                {pricingModels.map((model) => (
                    <>
                        {org_id === config.paymentcloud.PAYCHEX_ORG_ID &&
                        model.type === "surcharge" ? (
                            <></>
                        ) : (activePricingModels && activePricingModels.includes(model.type)) ? (
                            <button
                                disabled={!isEditable}
                                type="button"
                                key={model.label}
                                className={
                                    selectedModel == model.type ? "active" : ""
                                }
                                onClick={() => handlePricingModelChange(model)}
                            >
                                {selectedModel == model.type ? (
                                    <IoMdCheckmark />
                                ) : (
                                    ""
                                )}{" "}
                                {model.label}
                            </button>
                        ) : null}
                    </>
                ))}
                {activePricingModels && activePricingModels.includes("ach") && (
                    <button
                        type="button"
                        className={selectedModel == "ach" ? "active" : ""}
                        onClick={() =>
                            handlePricingModelChange({ type: "ach", label: "ACH" })
                        }
                    >
                        {selectedModel == "ach" ? <IoMdCheckmark /> : ""} ACH
                    </button>
                )}
            </Stack>
            {errors?.pricing_model &&
                Array.isArray(errors.pricing_model) &&
                errors.pricing_model.map((error) => (
                    <span className="error-message">{error}</span>
                ))}
            {org_id === config.paymentcloud.EMS_ORG_ID && (
                <Stack className="mt-4r">
                    <p className="sub-heading-lead">
                        Pricing preapproved by the Rate Desk?
                    </p>
                    <ToggleSwitch
                        handleChange={(_e, value) =>
                            setFormDataByKeyValue(
                                "pricing_approved_by_rate_desk",
                                value,
                            )
                        }
                        value={form_data.pricing_approved_by_rate_desk}
                        options={[
                            { key: "yes", value: "Yes" },
                            { key: "no", value: "No" },
                        ]}
                        name="pricing_approved_by_rate_desk"
                    />
                </Stack>
            )}
            {renderModel(
                selectedModel,
                emsEquipments,
                setFormDataByKeyValue,
                errors,
                form_data,
                allEquipments,
                removeError,
                isEditable,
            )}
        </div>
    );
};

export default PricingModel;
